import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  LockKeyhole,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/common/Button";
import { Field } from "@/components/application-form/Field";
import { formatApiError } from "@/services/api";
import { authService } from "@/services/authService";
import SEO from "@/components/SEO";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  /**
   * =====================================================
   * STEP
   *
   * 1 = Email
   * 2 = OTP
   * 3 = New Password
   * =====================================================
   */

  const [step, setStep] = useState(1);

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  const [busy, setBusy] = useState(false);

  /**
   * =====================================================
   * EMAIL
   * =====================================================
   */

  const [email, setEmail] = useState("");

  /**
   * =====================================================
   * OTP
   * =====================================================
   */

  const [otp, setOtp] = useState("");

  /**
   * =====================================================
   * RESET TOKEN
   *
   * Backend OTP verification ke baad resetToken
   * return karta hai.
   *
   * Isi token ko password reset ke waqt backend ko
   * bhejna zaroori hai.
   * =====================================================
   */

  const [resetToken, setResetToken] = useState("");

  /**
   * =====================================================
   * PASSWORD
   * =====================================================
   */

  const [password, setPassword] = useState("");

  const [confirmPassword, setConfirmPassword] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  /**
   * =====================================================
   * RESEND TIMER
   * =====================================================
   */

  const [resendTimer, setResendTimer] =
    useState(0);

  /**
   * =====================================================
   * OTP RESEND COUNTDOWN
   * =====================================================
   */

  useEffect(() => {
    if (resendTimer <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendTimer((current) => {
        if (current <= 1) {
          clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  /**
   * =====================================================
   * SEND FORGOT PASSWORD OTP
   * =====================================================
   */

  async function handleSendOTP(e) {
    e.preventDefault();

    const normalizedEmail =
      email.trim().toLowerCase();

    if (!normalizedEmail) {
      toast.error(
        "Please enter your email address."
      );
      return;
    }

    setBusy(true);

    try {
      const response =
        await authService.forgotPassword(
          normalizedEmail
        );

      setEmail(normalizedEmail);

      /**
       * Clear any old reset session
       */
      setOtp("");
      setResetToken("");
      setPassword("");
      setConfirmPassword("");

      setStep(2);

      setResendTimer(
        response?.resendAvailableInSeconds || 30
      );

      toast.success(
        response?.message ||
          "OTP has been sent to your email."
      );
    } catch (error) {
      toast.error(
        formatApiError(error)
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * =====================================================
   * VERIFY OTP
   * =====================================================
   */

  async function handleVerifyOTP(e) {
    e.preventDefault();

    const cleanOTP =
      otp.trim();

    if (!cleanOTP) {
      toast.error(
        "Please enter the OTP."
      );
      return;
    }

    if (!/^\d{6}$/.test(cleanOTP)) {
      toast.error(
        "Please enter a valid 6-digit OTP."
      );
      return;
    }

    setBusy(true);

    try {
      const response =
        await authService.verifyForgotPasswordOTP(
          email,
          cleanOTP
        );

      /**
       * =================================================
       * IMPORTANT
       *
       * Backend OTP verification response:
       *
       * {
       *   verified: true,
       *   resetToken: "...",
       *   expiresInSeconds: 600
       * }
       *
       * Password reset ke liye ye resetToken
       * required hai.
       * =================================================
       */

      if (!response?.resetToken) {
        throw new Error(
          "Password reset session could not be created. Please request a new OTP."
        );
      }

      /**
       * Save reset token for STEP 3
       */
      setResetToken(
        response.resetToken
      );

      /**
       * OTP ki zaroorat ab nahi hai
       * lekin state me rehne dena bhi harmful nahi.
       * Reset ke baad clear karenge.
       */

      setStep(3);

      toast.success(
        response?.message ||
          "OTP verified successfully."
      );
    } catch (error) {
      toast.error(
        formatApiError(error)
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * =====================================================
   * RESEND OTP
   * =====================================================
   */

  async function handleResendOTP() {
    if (
      resendTimer > 0 ||
      busy
    ) {
      return;
    }

    setBusy(true);

    try {
      const response =
        await authService.resendForgotPasswordOTP(
          email
        );

      /**
       * New OTP means old reset session
       * should not be used.
       */
      setOtp("");
      setResetToken("");

      setResendTimer(
        response?.resendAvailableInSeconds ||
          30
      );

      toast.success(
        response?.message ||
          "A new OTP has been sent to your email."
      );
    } catch (error) {
      toast.error(
        formatApiError(error)
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * =====================================================
   * RESET PASSWORD
   * =====================================================
   */

  async function handleResetPassword(e) {
    e.preventDefault();

    /**
     * =================================================
     * FRONTEND VALIDATION
     * =================================================
     */

    if (!password) {
      toast.error(
        "Please enter a new password."
      );
      return;
    }

    if (password.length < 8) {
      toast.error(
        "Password must be at least 8 characters."
      );
      return;
    }

    if (!confirmPassword) {
      toast.error(
        "Please confirm your new password."
      );
      return;
    }

    if (password !== confirmPassword) {
      toast.error(
        "Passwords do not match."
      );
      return;
    }

    /**
     * =================================================
     * RESET TOKEN CHECK
     *
     * OTP verification ke baad resetToken milna
     * mandatory hai.
     * =================================================
     */

    if (!resetToken) {
      toast.error(
        "Password reset session has expired. Please start again."
      );
      return;
    }

    setBusy(true);

    try {
      /**
       * =================================================
       * IMPORTANT
       *
       * authService.resetPassword expects:
       *
       * resetPassword(
       *   email,
       *   resetToken,
       *   newPassword
       * )
       *
       * NOT an object.
       * =================================================
       */

      const response =
        await authService.resetPassword(
          email,
          resetToken,
          password
        );

      toast.success(
        response?.message ||
          "Password reset successfully."
      );

      /**
       * =================================================
       * CLEAR SENSITIVE DATA
       * =================================================
       */

      setResetToken("");
      setOtp("");
      setPassword("");
      setConfirmPassword("");

      /**
       * =================================================
       * REDIRECT TO LOGIN
       * =================================================
       */

      setTimeout(() => {
        navigate("/login");
      }, 700);

    } catch (error) {
      toast.error(
        formatApiError(error)
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * =====================================================
   * BACK TO LOGIN
   * =====================================================
   */

  function backToLogin() {
    navigate("/login");
  }

  /**
   * =====================================================
   * CHANGE EMAIL
   * =====================================================
   */

  function changeEmail() {
    setStep(1);

    setOtp("");

    /**
     * Clear reset token
     */
    setResetToken("");

    setPassword("");

    setConfirmPassword("");

    setResendTimer(0);
  }

  return (
    <>
      <SEO page="forgot-password" />

      <div
        data-testid="forgot-password-page"
        className="w-full"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            {step === 1 && (
              <Mail className="h-6 w-6" />
            )}

            {step === 2 && (
              <ShieldCheck className="h-6 w-6" />
            )}

            {step === 3 && (
              <LockKeyhole className="h-6 w-6" />
            )}
          </div>

          <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">
            {step === 1 &&
              "Forgot Password?"}

            {step === 2 &&
              "Verify OTP"}

            {step === 3 &&
              "Create New Password"}
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            {step === 1 &&
              "Enter your registered email address and we'll send you an OTP."}

            {step === 2 &&
              "Enter the 6-digit OTP sent to your registered email address."}

            {step === 3 &&
              "Create a strong new password for your CET account."}
          </p>
        </div>

        {/* =================================================
            STEP INDICATOR
        ================================================= */}

        <div className="mt-7 flex items-center justify-center gap-2">
          <StepIndicator
            number="1"
            active={step === 1}
            completed={step > 1}
          />

          <div
            className={`h-px w-10 ${
              step > 1
                ? "bg-amber-500"
                : "bg-slate-200"
            }`}
          />

          <StepIndicator
            number="2"
            active={step === 2}
            completed={step > 2}
          />

          <div
            className={`h-px w-10 ${
              step > 2
                ? "bg-amber-500"
                : "bg-slate-200"
            }`}
          />

          <StepIndicator
            number="3"
            active={step === 3}
            completed={false}
          />
        </div>

        {/* =================================================
            STEP 1
        ================================================= */}

        {step === 1 && (
          <form
            onSubmit={handleSendOTP}
            className="mt-8 space-y-5"
          >
            <Field
              label="Registered Email"
              name="email"
              type="email"
              value={email}
              onChange={(name, value) =>
                setEmail(value)
              }
              placeholder="Enter your registered email"
              required
              autoComplete="email"
              testId="forgot-email"
            />

            <Button
              as="button"
              type="submit"
              className="w-full"
              disabled={busy}
              data-testid="forgot-send-otp-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Send OTP
                </>
              )}
            </Button>
          </form>
        )}

        {/* =================================================
            STEP 2
        ================================================= */}

        {step === 2 && (
          <form
            onSubmit={handleVerifyOTP}
            className="mt-8 space-y-5"
          >
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <p className="text-center text-sm leading-5 text-blue-800">
                OTP sent to{" "}
                <strong>
                  {email}
                </strong>
              </p>
            </div>

            <Field
              label="6-Digit OTP"
              name="otp"
              type="text"
              value={otp}
              onChange={(name, value) =>
                setOtp(
                  value
                    .replace(/\D/g, "")
                    .slice(0, 6)
                )
              }
              placeholder="Enter 6-digit OTP"
              required
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              testId="forgot-otp"
            />

            <Button
              as="button"
              type="submit"
              className="w-full"
              disabled={
                busy ||
                otp.length !== 6
              }
              data-testid="forgot-verify-otp-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" />
                  Verify OTP
                </>
              )}
            </Button>

            {/* RESEND */}

            <div className="text-center">
              {resendTimer > 0 ? (
                <p className="text-sm text-slate-500">
                  Resend OTP in{" "}
                  <span className="font-semibold text-slate-700">
                    {resendTimer}s
                  </span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={busy}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Resend OTP
                </button>
              )}
            </div>

            {/* CHANGE EMAIL */}

            <div className="text-center">
              <button
                type="button"
                onClick={changeEmail}
                disabled={busy}
                className="text-sm text-slate-500 hover:text-slate-800 hover:underline"
              >
                Change email address
              </button>
            </div>
          </form>
        )}

        {/* =================================================
            STEP 3
        ================================================= */}

        {step === 3 && (
          <form
            onSubmit={handleResetPassword}
            className="mt-8 space-y-5"
          >
            <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-medium text-green-800">
                <CheckCircle2 className="h-4 w-4" />
                Email verified successfully
              </div>
            </div>

            {/* PASSWORD */}

            <div className="relative">
              <Field
                label="New Password"
                name="password"
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                value={password}
                onChange={(name, value) =>
                  setPassword(value)
                }
                placeholder="Enter new password"
                required
                autoComplete="new-password"
                testId="forgot-new-password"
              />

              <button
                type="button"
                onClick={() =>
                  setShowPassword(
                    (value) => !value
                  )
                }
                className="absolute right-3 top-[38px] text-slate-400 hover:text-slate-700"
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* CONFIRM PASSWORD */}

            <div className="relative">
              <Field
                label="Confirm New Password"
                name="confirmPassword"
                type={
                  showConfirmPassword
                    ? "text"
                    : "password"
                }
                value={confirmPassword}
                onChange={(name, value) =>
                  setConfirmPassword(value)
                }
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
                testId="forgot-confirm-password"
              />

              <button
                type="button"
                onClick={() =>
                  setShowConfirmPassword(
                    (value) => !value
                  )
                }
                className="absolute right-3 top-[38px] text-slate-400 hover:text-slate-700"
                aria-label={
                  showConfirmPassword
                    ? "Hide password"
                    : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            <p className="text-xs leading-5 text-slate-500">
              Password must contain at least 8
              characters.
            </p>

            <Button
              as="button"
              type="submit"
              className="w-full"
              disabled={busy}
              data-testid="forgot-reset-password-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                <>
                  <LockKeyhole className="h-4 w-4" />
                  Reset Password
                </>
              )}
            </Button>
          </form>
        )}

        {/* =================================================
            BACK TO LOGIN
        ================================================= */}

        <div className="mt-7 text-center">
          <button
            type="button"
            onClick={backToLogin}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>
        </div>

        {/* =================================================
            FOOTER
        ================================================= */}

        {step === 1 && (
          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link
              to="/apply"
              className="font-semibold text-amber-600 hover:underline"
            >
              Apply for the exam
            </Link>
          </p>
        )}
      </div>
    </>
  );
}

/**
 * =====================================================
 * STEP INDICATOR
 * =====================================================
 */

function StepIndicator({
  number,
  active,
  completed,
}) {
  return (
    <div
      className={`
        flex h-8 w-8 items-center justify-center
        rounded-full border text-xs font-bold
        transition-all
        ${
          active
            ? "border-amber-500 bg-amber-500 text-white"
            : completed
            ? "border-amber-500 bg-amber-50 text-amber-600"
            : "border-slate-200 bg-white text-slate-400"
        }
      `}
    >
      {completed ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        number
      )}
    </div>
  );
}